// directives/when-seen.ts
import { html, noChange, render } from "lit";
import { AsyncDirective, directive } from "lit/async-directive.js";
import { PartType } from "lit/directive.js";
import { cache } from "lit/directives/cache.js";
import { Subject, debounceTime, filter, map } from "rxjs";
var isInViewport = (elem) => {
  const { top, left, bottom, right } = elem.getBoundingClientRect();
  if (top === 0 && left === 0 && bottom === 0 && right === 0)
    return false;
  return top >= 0 && left >= 0 && bottom <= (window.innerHeight || document.documentElement.clientHeight) && right <= (window.innerWidth || document.documentElement.clientWidth);
};
var renderStatus = /* @__PURE__ */ new WeakMap();
var divMap = /* @__PURE__ */ new WeakMap();
var subscriptionCache = /* @__PURE__ */ new WeakMap();
var intersectionObserverMap = /* @__PURE__ */ new Map();
var GLOBAL_MAP_KEY = "global";
var interobs = new Subject();
intersectionObserverMap.set(GLOBAL_MAP_KEY, {
  intersectingUpdates: interobs,
  intersectionObserver: new IntersectionObserver((intersectingEntries) => interobs.next(intersectingEntries), {
    threshold: [0, 1]
  })
});
var IntersectionDirective = class extends AsyncDirective {
  constructor(partInfo) {
    super(partInfo);
    if (partInfo.type !== PartType.CHILD)
      throw new Error("whenSeen/virtualize can only be used as a ChildPart");
    this._part = partInfo;
  }
};
var WhenSeenDirective = class extends IntersectionDirective {
  render(seenCallback, unseenCallback, operatorFunctions = [debounceTime(50)]) {
    this._seenCallback = seenCallback;
    this._unseenCallback = unseenCallback;
    const { intersectingUpdates, intersectionObserver } = intersectionObserverMap.get(GLOBAL_MAP_KEY);
    this._intersectionObserver = intersectionObserver;
    const part = this._part;
    this._node = part.startNode.parentElement;
    this._intersectionSubscriber = intersectingUpdates.pipe(
      filter((entries) => entries.some((entry) => entry.target === this._node)),
      ...operatorFunctions
    ).subscribe(
      (entries) => entries.some((entry) => entry.isIntersecting) ? this._renderElement() : this._unrenderElement()
    );
    this._intersectionObserver.observe(this._node);
    if (subscriptionCache.has(part))
      return noChange;
    subscriptionCache.set(part, {});
    subscriptionCache.get(part).teardown = this._teardownLogic;
    return noChange;
  }
  disconnected() {
    this._teardownLogic();
  }
  _teardownLogic() {
    if (this._node)
      this._intersectionObserver?.unobserve(this._node);
    this._intersectionSubscriber?.unsubscribe();
  }
  _renderElement() {
    if (renderStatus.get(this._part) === true)
      return;
    renderStatus.set(this._part, true);
    this._seenCallback();
  }
  _unrenderElement() {
    if (renderStatus.get(this._part) === false || // dont unrender if it has already been unrendered
    !renderStatus.has(this._part))
      return;
    renderStatus.set(this._part, false);
    requestAnimationFrame(() => {
      if (renderStatus.get(this._part) === true)
        return;
      this._unseenCallback();
    });
  }
};
var whenSeen = directive(WhenSeenDirective);
var VirtualizeDirective = class extends IntersectionDirective {
  constructor() {
    super(...arguments);
    this._wrapperDisplayContents = false;
    this._visibleState = /* @__PURE__ */ new WeakMap();
  }
  render(content, shimmer = (oldHeight) => html` <div style="height: ${oldHeight}px"></div> `, {
    intersectionObserverOptions = { groupKey: GLOBAL_MAP_KEY },
    dontUnload = false,
    onRender,
    onUnrender,
    wrapperElem,
    saveHeightBeforeUnload = true,
    wrapperDisplayContents = false
  } = {}) {
    this._options = {
      intersectionObserverOptions,
      dontUnload,
      onRender,
      onUnrender,
      wrapperElem,
      saveHeightBeforeUnload,
      wrapperDisplayContents
    };
    this._shimmer = shimmer;
    this._content = content;
    const part = this._part;
    this._wrapperDisplayContents = wrapperDisplayContents;
    if (!intersectionObserverMap.has(intersectionObserverOptions.groupKey)) {
      const obssubj = new Subject();
      intersectionObserverMap.set(intersectionObserverOptions.groupKey, {
        intersectingUpdates: obssubj,
        intersectionObserver: new IntersectionObserver((intersectingEntries) => obssubj.next(intersectingEntries), {
          threshold: [0, 1],
          ...intersectionObserverOptions
        })
      });
    }
    const { intersectingUpdates, intersectionObserver } = intersectionObserverMap.get(
      intersectionObserverOptions.groupKey
    );
    this._intersectionObserver = intersectionObserver;
    if (!divMap.has(part)) {
      let wrapper = wrapperElem?.();
      if (!wrapper) {
        wrapper = document.createElement("div");
        if (this._wrapperDisplayContents)
          wrapper.style.display = "contents";
      } else {
        this._wrapperDisplayContents = wrapper.style.display === "contents";
      }
      divMap.set(part, wrapper);
    }
    this._node = divMap.get(part);
    if (subscriptionCache.has(part)) {
      this._reRenderElement();
      if (renderStatus.get(part) && this._options.onRender)
        this._options.onRender();
      return this._node;
    }
    subscriptionCache.set(part, { content: dontUnload ? content : shimmer(64) });
    render(dontUnload ? content : shimmer(64), this._node);
    setTimeout(() => {
      this._intersectionSubscriber = intersectingUpdates.pipe(
        map((entries) => {
          const intersectionElements = this._wrapperDisplayContents ? Array.from(this._node.children) : [this._node];
          return {
            entries: entries.filter((entry) => intersectionElements.includes(entry.target)),
            elements: intersectionElements
          };
        }),
        // if the previous map returns an empty array the update does not concern us
        filter(({ entries }) => !!entries.length)
      ).subscribe(({ entries, elements }) => {
        entries.forEach((entry) => this._visibleState.set(entry.target, entry.isIntersecting));
        if (elements.some((element) => this._visibleState.get(element))) {
          this._renderElement();
        } else {
          this._unrenderElement();
        }
      });
      if (this._wrapperDisplayContents) {
        let nodes = [];
        const mutationObserver = new MutationObserver((wat) => {
          nodes.forEach((childNode) => this._unobserve(childNode));
          nodes = Array.from(this._node.children);
          nodes.forEach((childNode) => this._intersectionObserver.observe(childNode));
        });
        mutationObserver.observe(this._node, { childList: true, attributes: false, subtree: true });
        nodes = Array.from(this._node.children);
        nodes.forEach((childNode) => this._intersectionObserver.observe(childNode));
      } else
        this._intersectionObserver.observe(this._node);
      subscriptionCache.get(part).teardown = this._teardownLogic;
    }, 50);
    return this._node;
  }
  disconnected() {
    this._teardownLogic();
  }
  /**
   * ! assumes that the child nodes will be the same since we don't refresh the intersection observer's watch list due to performance reasons
   */
  _reRenderElement() {
    if (renderStatus.get(this._part) !== true)
      return;
    subscriptionCache.get(this._part).content = this._content;
    render(cache(subscriptionCache.get(this._part).content), this._node);
  }
  _renderElement() {
    if (renderStatus.get(this._part) === true)
      return;
    renderStatus.set(this._part, true);
    subscriptionCache.get(this._part).content = this._content;
    const oldNodes = Array.from(this._node.children);
    render(cache(subscriptionCache.get(this._part).content), this._node);
    setTimeout(() => {
      if (!renderStatus.get(this._part))
        return;
      oldNodes.forEach((childNode) => this._unobserve(childNode));
      Array.from(this._node.children).forEach((childNode) => this._intersectionObserver.observe(childNode));
    }, 50);
    if (this._options.onRender)
      this._options.onRender();
  }
  _unrenderElement() {
    if (renderStatus.get(this._part) === false || // dont unrender if it has already been unrendered
    !renderStatus.has(this._part) || // dont unrender if it has never been rendered
    this._options.dontUnload)
      return;
    renderStatus.set(this._part, false);
    const oldHeight = this._wrapperDisplayContents ? Array.from(this._node.children).reduce((acc, node) => Math.max(acc, node.offsetHeight), -1) : this._node.offsetHeight;
    subscriptionCache.get(this._part).content = this._shimmer(oldHeight);
    if (renderStatus.get(this._part) === true)
      return;
    const oldNodes = Array.from(this._node.children);
    render(this._shimmer(oldHeight), this._node);
    setTimeout(() => {
      if (renderStatus.get(this._part))
        return;
      oldNodes.forEach((childNode) => this._unobserve(childNode));
      Array.from(this._node.children).forEach((childNode) => {
        this._intersectionObserver.observe(childNode);
      });
    }, 10);
    if (this._options.onUnrender)
      this._options.onUnrender();
  }
  _teardownLogic() {
    if (this._node)
      if (this._wrapperDisplayContents)
        Array.from(this._node.children).forEach((childNode) => this._unobserve(childNode));
      else
        this._unobserve(this._node);
    this._intersectionSubscriber?.unsubscribe();
  }
  _unobserve(element) {
    this._intersectionObserver?.unobserve(element);
    this._visibleState.delete(element);
  }
};
var virtualize = directive(VirtualizeDirective);
export {
  GLOBAL_MAP_KEY,
  isInViewport,
  virtualize,
  whenSeen
};
